import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { Crown, CreditCard, Calendar, Clock, ArrowSquareOut, Lightning } from "@phosphor-icons/react";
import { toast } from "sonner";

/**
 * Subscription Management Panel — drop-in section for /profile.
 * Reads /api/pro/me and renders status, dates, and the correct CTA
 * (start subscription / manage subscription / update payment).
 */
export default function SubscriptionPanel() {
  const { lang } = useI18n();
  const [status, setStatus] = useState(null);
  const [opening, setOpening] = useState(false);

  useEffect(() => {
    let alive = true;
    api.get("/pro/me")
      .then((r) => { if (alive) setStatus(r.data); })
      .catch(() => { if (alive) setStatus({ subscription_status: "none", is_pro: false }); });
    return () => { alive = false; };
  }, []);

  const openPortal = async () => {
    setOpening(true);
    try {
      const { data } = await api.post("/pro/portal");
      window.location.href = data.portal_url;
    } catch (e) {
      const msg = e.response?.data?.detail || "";
      toast.error(
        lang === "da"
          ? `Kunne ikke åbne Stripe-portalen. ${msg}`
          : `Couldn't open Stripe portal. ${msg}`
      );
      setOpening(false);
    }
  };

  if (!status) {
    return (
      <section
        className="mt-10 border border-[#262626] bg-[#141414] p-6"
        data-testid="subscription-panel-loading"
      >
        <div className="label-overline">{lang === "da" ? "Pro Crew" : "Pro Crew"}</div>
        <div className="h-12 mt-3 bg-[#0A0A0A] animate-pulse" />
      </section>
    );
  }

  const isPro = !!status.is_pro;
  const subStatus = status.subscription_status || "none";
  const inTrial = !!status.in_trial;
  const cancelAtEnd = !!status.cancel_at_period_end;
  const subType = status.subscription_type; // monthly | annual | null
  const hasStripeSub = !!status.stripe_subscription_id;

  const locale = lang === "da" ? "da-DK" : "en-US";
  const fmtDate = (iso) => iso
    ? new Date(iso).toLocaleDateString(locale, { day: "2-digit", month: "short", year: "numeric" })
    : "—";

  const planLabel = (() => {
    if (subType === "monthly") return lang === "da" ? "Pro Crew · Månedlig" : "Pro Crew · Monthly";
    if (subType === "annual") return lang === "da" ? "Pro Crew · Årlig" : "Pro Crew · Annual";
    if (inTrial) return lang === "da" ? "Pro Crew · Gratis prøveperiode" : "Pro Crew · Free trial";
    if (isPro) return lang === "da" ? "Pro Crew" : "Pro Crew";
    return lang === "da" ? "Gratis konto" : "Free account";
  })();

  const statusPill = (() => {
    if (inTrial) {
      const n = Math.max(0, status.trial_days_left || 0);
      return {
        label: lang === "da" ? `Prøve · ${n} dag${n === 1 ? "" : "e"} tilbage` : `Trial · ${n} day${n === 1 ? "" : "s"} left`,
        color: "#FFCC00",
        testid: "sub-status-trialing",
      };
    }
    if (subStatus === "active") {
      return {
        label: cancelAtEnd
          ? (lang === "da" ? "Aktiv · Annulleres ved periodens slutning" : "Active · Cancels at period end")
          : (lang === "da" ? "Aktiv" : "Active"),
        color: cancelAtEnd ? "#FF9F0A" : "#34C759",
        testid: "sub-status-active",
      };
    }
    if (subStatus === "past_due") {
      return {
        label: lang === "da" ? "Betaling fejlede" : "Payment failed",
        color: "#FF3B30",
        testid: "sub-status-past-due",
      };
    }
    if (subStatus === "cancelled" || subStatus === "expired") {
      return {
        label: lang === "da" ? "Udløbet" : "Ended",
        color: "#8E8E93",
        testid: "sub-status-ended",
      };
    }
    return {
      label: lang === "da" ? "Ingen aktiv plan" : "No active plan",
      color: "#8E8E93",
      testid: "sub-status-none",
    };
  })();

  // Choose the "primary date" to show prominently.
  const primaryDate = (() => {
    if (inTrial) {
      return {
        label: lang === "da" ? "Prøveperioden slutter" : "Trial ends",
        value: fmtDate(status.trial_end_date),
        Icon: Clock,
      };
    }
    if (subStatus === "active" && status.next_billing_date) {
      return {
        label: cancelAtEnd
          ? (lang === "da" ? "Pro forbliver aktiv til" : "Pro stays active until")
          : (lang === "da" ? "Næste betaling" : "Next billing"),
        value: fmtDate(status.next_billing_date),
        Icon: Calendar,
      };
    }
    if (status.entitlement_until) {
      return {
        label: lang === "da" ? "Pro aktiv til" : "Pro active until",
        value: fmtDate(status.entitlement_until),
        Icon: Calendar,
      };
    }
    return null;
  })();

  return (
    <section
      className={`mt-10 border p-6 ${isPro ? "border-[#FFCC00]/40 bg-[#FFCC00]/[0.04]" : "border-[#262626] bg-[#141414]"}`}
      data-testid="subscription-panel"
    >
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <Crown weight={isPro ? "fill" : "duotone"} size={26} color={isPro ? "#FFCC00" : "#8E8E93"} />
          <div>
            <div className="label-overline" style={{ color: isPro ? "#FFCC00" : undefined }}>
              {lang === "da" ? "Mit abonnement" : "My subscription"}
            </div>
            <div className="font-display text-2xl tracking-tight mt-1" data-testid="sub-plan-label">
              {planLabel.toUpperCase()}
            </div>
          </div>
        </div>
        <span
          className="px-3 py-1.5 text-[10px] font-bold uppercase tracking-widest border"
          style={{ color: statusPill.color, borderColor: `${statusPill.color}66`, background: `${statusPill.color}1A` }}
          data-testid={statusPill.testid}
        >
          {statusPill.label}
        </span>
      </div>

      {/* Date block */}
      {primaryDate && (
        <div
          className="mt-5 inline-flex items-center gap-3 border border-[#262626] bg-[#0A0A0A] px-4 py-3"
          data-testid="sub-primary-date"
        >
          <primaryDate.Icon weight="duotone" size={20} color={isPro ? "#FFCC00" : "#8E8E93"} />
          <div className="text-xs">
            <div className="label-overline">{primaryDate.label}</div>
            <div className="font-mono-tag text-sm text-white mt-0.5" data-testid="sub-primary-date-value">
              {primaryDate.value}
            </div>
          </div>
        </div>
      )}

      {/* Past-due banner */}
      {subStatus === "past_due" && (
        <div
          className="mt-5 border border-[#FF3B30]/40 bg-[#FF3B30]/10 px-4 py-3 text-sm"
          data-testid="sub-past-due-banner"
        >
          {lang === "da"
            ? "Vi kunne ikke opkræve dit kort. Opdater betalingsmetoden for at beholde Pro."
            : "We couldn't charge your card. Update your payment method to keep Pro."}
        </div>
      )}

      {/* Actions */}
      <div className="mt-6 flex flex-wrap gap-3 items-center">
        {hasStripeSub && (subStatus === "active" || subStatus === "trialing" || subStatus === "past_due") ? (
          <button
            type="button"
            onClick={openPortal}
            disabled={opening}
            data-testid="sub-manage-btn"
            className="kinetic-btn bg-[#FFCC00] hover:bg-[#e6b800] disabled:opacity-50 text-black px-4 py-3 font-bold uppercase tracking-widest text-xs flex items-center gap-2"
          >
            <CreditCard weight="bold" size={14} />
            {opening
              ? (lang === "da" ? "Åbner…" : "Opening…")
              : (subStatus === "past_due"
                ? (lang === "da" ? "Opdater betaling" : "Update payment")
                : (lang === "da" ? "Administrér abonnement" : "Manage subscription"))}
            <ArrowSquareOut weight="bold" size={12} />
          </button>
        ) : (
          <Link
            to="/pro"
            data-testid="sub-upgrade-cta"
            className="kinetic-btn bg-[#FFCC00] hover:bg-[#e6b800] text-black px-4 py-3 font-bold uppercase tracking-widest text-xs flex items-center gap-2"
          >
            <Lightning weight="fill" size={14} />
            {status.trial_used
              ? (lang === "da" ? "Bliv Pro" : "Go Pro")
              : (lang === "da" ? "Start 14 dages gratis prøve" : "Start 14-day free trial")}
          </Link>
        )}

        {isPro && (
          <Link
            to="/analytics"
            data-testid="sub-analytics-link"
            className="text-xs uppercase tracking-widest font-bold text-[#FFCC00] hover:underline"
          >
            {lang === "da" ? "Se Pro analytics →" : "View Pro analytics →"}
          </Link>
        )}
      </div>

      {/* Tiny footer hint */}
      <p className="text-[11px] text-zinc-500 mt-5 leading-relaxed max-w-2xl">
        {hasStripeSub
          ? (lang === "da"
            ? "Stripe Customer Portal lader dig opdatere kort, skifte plan eller annullere. Annullering bevarer Pro til periodens slutning."
            : "The Stripe Customer Portal lets you update your card, switch plans, or cancel. Cancelling keeps Pro until the period ends.")
          : (lang === "da"
            ? "Sikker betaling via Stripe. Du kan opsige til enhver tid fra denne side."
            : "Secure payment via Stripe. Cancel anytime from this page.")}
      </p>
    </section>
  );
}
